import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ProtectedRoute from '../../routes/protected';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Chart } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from 'chart.js';
import performanceService from '../../services/data/performance.service';
import UsePerms from '../../context/userAccess';
import useCompanyData from '../../context/company';
import SkeletonTeam from './skeletonTeam';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const QUADRANT_LABELS = {
    1: 'Detratores',
    2: 'Questionáveis',
    3: 'Latentes',
    4: 'Eficazes',
    5: 'Transitórios',
    6: 'Emergentes',
    7: 'Comprometidos',
    8: 'Emergentes',
    9: 'Estrelas',
};

const STATUS_OPTIONS = [
    { value: '', label: 'Todos' },
    { value: 'PE', label: 'Pendente' },
    { value: 'EP', label: 'Em calibração' },
    { value: 'DO', label: 'Calibrado' },
];

function DistributionChart({ groups, title, hasCalibrations }) {
    if (!groups?.length) return <p className="text-muted small mb-0">Sem dados para exibir.</p>;

    const labels = groups.map((g) => g.name);
    const beforeData = groups.map((g) => Object.values(g.before || {}).reduce((a, b) => a + b, 0));
    const afterData = groups.map((g) => Object.values(g.after || {}).reduce((a, b) => a + b, 0));

    const data = {
        labels,
        datasets: [
            { label: 'Antes (original)', data: beforeData, backgroundColor: 'rgba(108, 117, 125, 0.7)' },
            { label: 'Depois (calibrado)', data: afterData, backgroundColor: 'rgba(81, 168, 177, 0.7)' },
        ],
    };

    const chartHeight = Math.min(500, Math.max(220, labels.length * 36));

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 24,
                right: 8,
                bottom: 8,
                left: 8,
            },
        },
        plugins: { legend: { position: 'top' } },
        scales: {
            y: {
                beginAtZero: true,
                grace: '8%',
            },
        },
        datasets: {
            bar: {
                maxBarThickness: 42,
                barPercentage: 0.45,
                categoryPercentage: 0.65,
            },
        },
    };

    return (
        <div>
            <h6 className="fw-bold">{title}</h6>
            {!hasCalibrations && (
                <p className="text-muted small mb-2">
                    Nenhuma calibração concluída ainda — a coluna &quot;Depois&quot; permanece em zero.
                </p>
            )}
            <div
                className="pt-2"
                style={{ position: 'relative', height: `${chartHeight}px`, maxHeight: '500px', paddingTop: '12px' }}
            >
                <Chart type="bar" data={data} options={chartOptions} />
            </div>
        </div>
    );
}

function QuadrantDistributionTable({ groups }) {
    if (!groups?.length) return null;
    return (
        <div className="table-responsive mt-3">
            <table className="table table-sm table-bordered">
                <thead className="table-light">
                    <tr>
                        <th>Grupo</th>
                        <th className="text-center">Total</th>
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((q) => (
                            <th key={q} className="text-center small">Q{q}<br /><span className="text-muted fw-normal">Ant/Dep</span></th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {groups.map((g) => (
                        <tr key={g.id}>
                            <td>{g.name}</td>
                            <td className="text-center">{g.total}</td>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((q) => (
                                <td key={q} className="text-center small">
                                    {(g.before?.[q] || 0)}/{(g.after?.[q] || 0)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function CalibrationCommitteeList() {
    const VerifyPerms = UsePerms();
    const { rootUser } = useCompanyData();
    const toast = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const [pageLoad, setPageLoad] = useState(true);
    const [templateId, setTemplateId] = useState(searchParams.get('template') || '');
    const [reviewId, setReviewId] = useState(searchParams.get('review') || '');
    const [templateList, setTemplateList] = useState([]);
    const [reviewList, setReviewList] = useState([]);
    const [config, setConfig] = useState(null);
    const [results, setResults] = useState([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [managerFilter, setManagerFilter] = useState('');
    const [managerList, setManagerList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState([]);
    const [distribution, setDistribution] = useState(null);
    const [distTab, setDistTab] = useState('manager');
    const [exporting, setExporting] = useState(false);

    const canAccess = VerifyPerms(['add_performancematrix']) || rootUser;
    const hasScope = Boolean(templateId || reviewId);
    const scopeParams = () => {
        const data = {};
        if (templateId) data.template_id = templateId;
        if (reviewId) data.review_id = reviewId;
        return data;
    };

    const filterParams = () => {
        const data = { ...scopeParams() };
        if (statusFilter) data.status = statusFilter;
        if (managerFilter) data.manager_id = managerFilter;
        return data;
    };

    useEffect(() => {
        loadFilterOptions();
    }, []);

    useEffect(() => {
        if (hasScope) {
            loadConfig();
            loadManagers();
            setManagerFilter('');
            setStatusFilter('');
        } else {
            setConfig(null);
            setManagerList([]);
            setManagerFilter('');
        }
    }, [templateId, reviewId]);

    useEffect(() => {
        if (hasScope) {
            loadList();
            loadDistribution();
        } else {
            setResults([]);
            setDistribution(null);
        }
    }, [templateId, reviewId]);

    function loadFilterOptions() {
        const companyId = localStorage.getItem('companyId');
        Promise.all([
            performanceService.listPerformanceTemplate(1, { company_id: companyId }),
            performanceService.listPerformanceReviewOnly(1, { company_id: companyId }),
        ]).then(([templatesRes, reviewsRes]) => {
            setTemplateList((templatesRes.results || [])
                .filter((item) => item.result_to_matrix)
                .map((item) => ({
                    title: item.title,
                    value: item.id,
                })));
            setReviewList((reviewsRes.results || [])
                .filter((item) => item.result_to_matrix)
                .map((item) => ({
                    title: item.title,
                    value: item.id,
                })));
        }).catch((error) => {
            console.log(error.response);
        }).finally(() => {
            setPageLoad(false);
        });
    }

    function updateSearchParams(nextTemplateId, nextReviewId) {
        const params = {};
        if (nextTemplateId) params.template = nextTemplateId;
        if (nextReviewId) params.review = nextReviewId;
        setSearchParams(params);
    }

    function loadManagers() {
        performanceService.listCalibrationManagers(scopeParams()).then((data) => {
            setManagerList((data.managers || []).map((item) => ({
                title: item.name,
                value: item.id,
            })));
        }).catch(() => {
            setManagerList([]);
        });
    }

    function loadConfig() {
        performanceService.getCalibrationConfig(scopeParams()).then((data) => {
            setConfig(data);
        }).catch((error) => {
            console.log(error.response);
            setConfig(null);
        });
    }

    function loadList() {
        if (!hasScope) {
            setResults([]);
            return;
        }
        setLoading(true);
        performanceService.listCalibrationCommittee(filterParams()).then((response) => {
            setResults(response.results || []);
        }).catch((error) => {
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: error.response?.data?.detail || JSON.stringify(error.response?.data),
                life: 5000,
            });
        }).finally(() => {
            setLoading(false);
        });
    }

    function saveConfigField(field, value) {
        performanceService.saveCalibrationConfig({
            ...scopeParams(),
            [field]: value,
        }).then((data) => {
            setConfig((prev) => ({ ...prev, ...data }));
            toast.current?.show({ severity: 'success', summary: 'Salvo', detail: 'Configuração atualizada', life: 2500 });
        }).catch((error) => {
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: error.response?.data?.detail || 'Erro ao salvar',
                life: 5000,
            });
        });
    }

    function loadDistribution() {
        performanceService.getCalibrationDistribution(filterParams()).then((data) => {
            setDistribution(data);
        }).catch(() => setDistribution(null));
    }

    function handleExport() {
        setExporting(true);
        const data = filterParams();

        performanceService.exportCalibration(data).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const label = config?.template_title || config?.scope_title || 'calibracao';
            link.setAttribute('download', `calibracao_${label}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }).catch(() => {
            toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Falha ao exportar', life: 4000 });
        }).finally(() => setExporting(false));
    }

    function loadHistory() {
        performanceService.getCalibrationHistory(scopeParams()).then((response) => {
            setHistory(response.history || []);
            setShowHistory(true);
        }).catch((error) => {
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: error.response?.data?.detail || 'Erro ao carregar histórico',
                life: 5000,
            });
        });
    }

    function calibrateLink(row) {
        const baseTemplateId = row.template_id || templateId || '0';
        const path = `/performance/calibration/${baseTemplateId}/${row.employee.id}/${row.assessment_id}/`;
        if (!row.template_id && (reviewId || config?.is_standalone)) {
            const rid = reviewId || config?.review_id;
            return `${path}?review=${rid}`;
        }
        return path;
    }

    function quadrantLabel(q) {
        if (!q) return '—';
        return `Q${q} - ${QUADRANT_LABELS[q] || q}`;
    }

    if (!canAccess) {
        return (
            <ProtectedRoute>
                <div className="container-fluid p-5">
                    <p>Sem permissão para acessar o comitê de calibração.</p>
                </div>
            </ProtectedRoute>
        );
    }

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />

            <div className="container-fluid">
                <div className="row flex-nowrap">
                    <div className="col text-bg-green p-3">
                        <Link to="/painel/" className="fs-6 fw-bold ps-5 text-decoration-none text-white">
                            <FontAwesomeIcon icon={solid('arrow-left')} /> Comitê de Calibração
                        </Link>
                    </div>
                </div>

                <div className="my-4 mx-5 py-3 rounded-top" style={{ minHeight: '500px' }}>
                    {pageLoad ? (
                        <SkeletonTeam />
                    ) : (
                        <div className='paddingBottom200'>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h4 className="text-green fw-bold mb-0">Comitê de Calibração</h4>
                            </div>

                            <div className="card p-4 mb-4">
                                <p className="fw-bold mb-2">Filtros</p>
                                {!hasScope && (
                                    <p className="text-muted small mb-3">
                                        Selecione um ciclo ou uma avaliação configurados para a Matriz 9Box
                                    </p>
                                )}
                                <form className="bg-light p-3 rounded mb-3">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Filtrar por Ciclo</label>
                                            <Dropdown
                                                filter
                                                placeholder="Selecionar"
                                                value={templateId}
                                                optionLabel="title"
                                                optionValue="value"
                                                options={templateList}
                                                onChange={(e) => {
                                                    setTemplateId(e.value || '');
                                                    setReviewId('');
                                                    updateSearchParams(e.value || '', '');
                                                }}
                                                className="w-100 rounded"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Filtrar por Avaliação</label>
                                            <Dropdown
                                                filter
                                                placeholder="Selecionar"
                                                value={reviewId}
                                                optionLabel="title"
                                                optionValue="value"
                                                options={reviewList}
                                                onChange={(e) => {
                                                    setReviewId(e.value || '');
                                                    setTemplateId('');
                                                    updateSearchParams('', e.value || '');
                                                }}
                                                className="w-100 rounded"
                                            />
                                        </div>
                                    </div>
                                </form>

                                {hasScope && (
                                    <div className="row g-3 align-items-end">
                                        <div className="col-md-6">
                                            {config?.is_standalone && (
                                                <span className="text-muted small">
                                                    Avaliação avulsa
                                                </span>
                                            )}
                                        </div>
                                        <div className="col-md-6 text-end d-flex gap-1 justify-content-end">
                                            <Button
                                                label="Exportar"
                                                className="p-button-sm rounded-pill"
                                                loading={exporting}
                                                onClick={handleExport}
                                            />
                                            <Button
                                                label="Histórico"
                                                className="p-button-sm rounded-pill"
                                                onClick={loadHistory}
                                            />
                                        </div>
                                    </div>
                                )}
                                {config && !config.is_standalone && (
                                    <div className="row g-3 mt-2 pt-3 border-top">
                                        <div className="col-md-4">
                                            <div className="form-check form-switch">
                                                <label className="form-switch-label">Travar etapa do gestor durante a calibração</label>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    checked={config.lock_manager_steps ?? true}
                                                    onChange={(e) => saveConfigField('lock_manager_steps', e.target.checked)}
                                                />
                                            </div>
                                            <p className="text-muted small mb-0 mt-1">
                                                Bloqueia a edição de notas na etapa do gestor enquanto o comitê calibra, evitando que o resultado original mude após a avaliação.
                                            </p>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-check form-switch">
                                                <label className="form-switch-label">Notificar gestor ao concluir calibração</label>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    checked={config.notify_manager_on_complete}
                                                    onChange={(e) => saveConfigField('notify_manager_on_complete', e.target.checked)}
                                                />
                                            </div>
                                            <p className="text-muted small mb-0 mt-1">
                                                Envia uma mensagem ao gestor do colaborador quando a calibração for finalizada, informando se houve ou não alterações no resultado.
                                            </p>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="form-check form-switch">
                                                <label className="form-switch-label">Mostrar quadrantes na notificação</label>
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    checked={config.notify_manager_show_details}
                                                    disabled={!config.notify_manager_on_complete}
                                                    onChange={(e) => saveConfigField('notify_manager_show_details', e.target.checked)}
                                                />
                                            </div>
                                            <p className="text-muted small mb-0 mt-1">
                                                Inclui na mensagem ao gestor o quadrante original e o calibrado (antes → depois). Desativado, a notificação apenas informa que houve mudanças, sem expor os valores.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {hasScope && (
                                <>
                                    <div className="card p-3 mb-3">
                                        <div className="row g-2 align-items-end">
                                            <div className="col-md-3">
                                                <label className="form-label">Status</label>
                                                <select
                                                    className="form-select"
                                                    value={statusFilter}
                                                    onChange={(e) => setStatusFilter(e.target.value)}
                                                >
                                                    {STATUS_OPTIONS.map((o) => (
                                                        <option key={o.value} value={o.value}>{o.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label">Gestor</label>
                                                <Dropdown
                                                    filter
                                                    showClear
                                                    placeholder="Todos"
                                                    value={managerFilter}
                                                    optionLabel="title"
                                                    optionValue="value"
                                                    options={managerList}
                                                    onChange={(e) => setManagerFilter(e.value || '')}
                                                    className="w-100 rounded"
                                                    disabled={managerList.length === 0}
                                                />
                                            </div>
                                            <div className="col-md-2">
                                            <button className="btn btn-success rounded-pill btn-sm px-4 py-2" onClick={() => { loadList(); loadDistribution(); }} loading={loading}>
                                                Filtrar
                                            </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card">
                                        <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Colaborador</th>
                                                        <th>Cargo</th>
                                                        <th>Gestor</th>
                                                        <th>Status</th>
                                                        <th>Versão</th>
                                                        <th>Quadrante Original</th>
                                                        <th>Quadrante Calibrado</th>
                                                        <th></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {results.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="8" className="text-center py-4 text-muted">
                                                                {loading ? 'Carregando...' : 'Nenhum colaborador elegível encontrado'}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        results.map((row) => (
                                                            <tr key={`${row.employee.id}-${row.assessment_id}`}>
                                                                <td>{row.employee.name}</td>
                                                                <td>{row.employee.jobposition?.title || '—'}</td>
                                                                <td>{row.employee.manager?.name || '—'}</td>
                                                                <td>
                                                                    <span className={`badge ${row.status === 'DO' ? 'bg-success' : row.status === 'EP' ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                                                        {row.status_display}
                                                                    </span>
                                                                </td>
                                                                <td>{row.version ? `v${row.version}` : '—'}</td>
                                                                <td>{quadrantLabel(row.original_quadrant)}</td>
                                                                <td>{quadrantLabel(row.calibrated_quadrant)}</td>
                                                                <td>
                                                                    {row.original_quadrant ? (
                                                                        <Link
                                                                            to={calibrateLink(row)}
                                                                            className={`btn btn-sm  ${row.status === 'DO' ? 'btn-outline-success rounded-pill' : 'btn-success rounded-pill'}`}
                                                                        >
                                                                            {row.status === 'DO' ? 'Ver calibração' : 'Calibrar'}
                                                                        </Link>
                                                                    ) : (
                                                                        <span className="text-muted small">—</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </>
                            )}

                            {hasScope && distribution && (
                                <div className="card p-4 my-4">
                                    <div className="d-flex gap-2 mb-3">
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${distTab === 'manager' ? 'btn-success' : 'btn-outline-success'}`}
                                            onClick={() => setDistTab('manager')}
                                        >
                                            Por Gestor
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-sm ${distTab === 'department' ? 'btn-success' : 'btn-outline-success'}`}
                                            onClick={() => setDistTab('department')}
                                        >
                                            Por Área
                                        </button>
                                    </div>
                                    <DistributionChart
                                        title={distTab === 'manager' ? 'Distribuição por Gestor' : 'Distribuição por Área'}
                                        groups={distTab === 'manager' ? distribution.by_manager : distribution.by_department}
                                        hasCalibrations={distribution.has_calibrations}
                                    />
                                    <QuadrantDistributionTable
                                        groups={distTab === 'manager' ? distribution.by_manager : distribution.by_department}
                                    />
                                </div>
                            )}

                        </div>
                    )}
                </div>
            </div>

            {showHistory && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-xl modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Histórico de Calibrações</h5>
                                <button type="button" className="btn-close" onClick={() => setShowHistory(false)} />
                            </div>
                            <div className="modal-body">
                                {history.length === 0 ? (
                                    <p className="text-muted">Nenhuma calibração registrada.</p>
                                ) : (
                                    history.map((h) => (
                                        <div key={h.session_id} className="border rounded p-3 mb-3">
                                            <div className="d-flex justify-content-between">
                                                <strong>{h.employee.name} <span className="badge bg-light text-dark">v{h.version}</span></strong>
                                                <small className="text-muted">
                                                    {h.calibrated_by} — {h.calibrated_at ? new Date(h.calibrated_at).toLocaleString('pt-BR') : ''}
                                                </small>
                                            </div>
                                            {h.committee_meeting_at && (
                                                <p className="mb-1 small text-muted">
                                                    Reunião: {new Date(h.committee_meeting_at).toLocaleString('pt-BR')}
                                                </p>
                                            )}
                                            {h.participants?.length > 0 && (
                                                <p className="mb-1 small">
                                                    Participantes: {h.participants.map((p) => p.name).join(', ')}
                                                </p>
                                            )}
                                            <p className="mb-1 small">
                                                {quadrantLabel(h.original_quadrant)} → {quadrantLabel(h.calibrated_quadrant)}
                                                {' '}| Nota: {h.original_final_score?.toFixed(2)} → {h.calibrated_final_score?.toFixed(2)}
                                            </p>
                                            <p className="mb-2 small"><em>{h.general_reason}</em></p>
                                            {h.items?.length > 0 && (
                                                <ul className="small mb-0">
                                                    {h.items.filter((i) => i.original_score !== i.calibrated_score).map((i, idx) => (
                                                        <li key={idx}>
                                                            {i.item_title}: {i.original_score} → {i.calibrated_score}
                                                            {i.justification && ` — ${i.justification}`}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowHistory(false)}>Fechar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ProtectedRoute>
    );
}
